// Native-shell bridge for the inonit Capacitor wrappers (Android + iOS).
//
// This module is safe to import unconditionally — every plugin call is
// guarded by `Capacitor.isNativePlatform()` so the regular web bundle is
// a no-op. The web app continues to work exactly as before.

import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { Keyboard } from "@capacitor/keyboard";
import { App } from "@capacitor/app";

/**
 * Initialize all native chrome (status bar, splash, keyboard, back button,
 * deep links). Call once from src/index.js before React renders.
 */
export function initNativeShell() {
  if (!Capacitor.isNativePlatform()) return;

  // Status bar: dark glyphs over the cream-paper top bar.
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  StatusBar.setBackgroundColor({ color: "#F4F0E1" }).catch(() => {});
  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});

  // Hide the launch splash as soon as the web app has mounted — the React
  // tree is up by the time this module is imported from src/index.js.
  // 250ms delay gives the first paint a chance to commit so we never see a
  // white flash between splash and content.
  setTimeout(() => {
    SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {});
  }, 250);

  // Keyboard: keep the bottom nav above the keyboard, but don't push the
  // whole webview when an input is just receiving focus on iOS.
  Keyboard.setAccessoryBarVisible({ isVisible: true }).catch(() => {});
  // Toggle a body class so we can use `.kb-open` in CSS if we ever need
  // very specific layout shifts.
  Keyboard.addListener("keyboardWillShow", () => {
    document.body.classList.add("kb-open");
  });
  Keyboard.addListener("keyboardWillHide", () => {
    document.body.classList.remove("kb-open");
  });

  // Android hardware back button → browser history back. Falls back to
  // App.exitApp() at the root of the stack. This is the canonical
  // Capacitor pattern; without it the back button is a no-op.
  App.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp().catch(() => {});
    }
  });

  // Deep links — handle inonit://… and https://inonit.vercel.app/* URLs
  // routed into the app. Native side is configured in AndroidManifest
  // intent-filters and iOS Associated Domains (see README-MOBILE.md).
  App.addListener("appUrlOpen", ({ url }) => {
    try {
      // Strip the origin so the React Router picks up just the path.
      const u = new URL(url);
      const target = u.pathname + u.search + u.hash;
      // Use history pushState so React Router catches it without a reload.
      window.history.pushState({}, "", target);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch {
      /* malformed URL — ignore */
    }
  });

  // Mark the body so CSS can target native-only tweaks (e.g. larger
  // tap targets, safe-area paddings on the top bar).
  document.body.classList.add("is-native", `is-${Capacitor.getPlatform()}`);
}
